import { Autocomplete, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import GoodCategoryService from '../../../../shared/services/good-category/good-category.service';
import { IEditFileUploadLineGood, IFileUploadLineGood } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import { IMeasurementUnit } from '../../../../shared/services/transaction/transaction.model';
import MeasurementUnitService from '../../../../shared/services/measurement-unit/measurement-unit.service';
import { IManufacturer } from '../../../../shared/services/manufacturer/manufacturer.model';
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service';
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model';
import { SupplierPicker } from '../../../presentation-components/form-field-components/supplier-picker';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import { formatTransaction } from '../../../../shared/utils/format-post-utils';

export const EditUploadLineGoodDialog: FunctionComponent<{
  editUploadLine: IFileUploadLineGood,
  open: boolean,
  closeHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineGood) => void,
  isSubmitting: boolean,
  manufacturers: IManufacturer[],
  errorCode?: number,
  errorMessage?: string,
}> = ({
  open,
  closeHandler,
  submitHandler,
  editUploadLine,
  isSubmitting,
  manufacturers,
  errorCode,
  errorMessage
}) => {
    //Services
    const measurementUnitService = new MeasurementUnitService()
    const goodCategoriesService = new GoodCategoryService()
    const customFieldService = new CustomFieldService()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [categoryOptions, setCategoryOptions] = useState<string[]>()
    const [subCategoryOneOptions, setSubCategorysOneOptions] = useState<string[]>()
    const [subCategoryTwoOptions, setSubCategoryTwoOptions] = useState<string[]>()
    const [categoryOnly, setCategoryOnly] = useState<boolean>(false)
    const [categoryAndSubCategoryOneOnly, setCategoryAndSubCategoryOneOnly] = useState<boolean>(false)
    const [goodCategoryId, setGoodCategoryId] = useState<string>(editUploadLine.goodCategoryId)
    const [measurementUnits, setMeasurementUnits] = useState<IMeasurementUnit[]>()
    const [suppliers, setSuppliers] = useState<IContactSupplierDetails[]>([])

    //Formik
    const capitalGoodSchema = Yup.object().shape({
      name: Yup.string().required(`Please give this ${screenLabelsContext.getLabel('product')} a name`),
      capitalGoodReference: Yup.string().nullable(),
      description: Yup.string().nullable(),
      modelNumber: Yup.string().required('You must provide a model number'),
      kgCarbonEmission: Yup.number().required('You must provide an emission amount'),
      manufacturerName: Yup.string().required(`You must provide a manufacturer`),
      typeOfGood: Yup.string().required('You must select a type of good'),
      unitName: Yup.string().required('You must provide a type of unit'),
      unitAmount: Yup.number().required('You must provide a unit amount'),
      goodCategoryId: Yup.string().required('You must select a category'),
      sourceOfEmission: Yup.string().nullable(),
      contactSupplierName: Yup.string().nullable(),
    })
    const formik = useFormik<any>({
      initialValues: {
        name: editUploadLine.name,
        description: editUploadLine.description,
        capitalGoodReference: editUploadLine.capitalGoodReference,
        category: '',
        subCategoryOne: '',
        subCategoryTwo: '',
        modelNumber: editUploadLine.modelNumber,
        kgCarbonEmission: editUploadLine.kgCarbonEmission,
        manufacturerName: editUploadLine.manufacturerName,
        typeOfGood: editUploadLine.typeOfGood,
        unitName: editUploadLine.unitName,
        unitAmount: editUploadLine.unitAmount,
        goodCategoryId: editUploadLine.goodCategoryId,
        sourceOfEmission: editUploadLine.sourceOfEmission,
        contactSupplierName: editUploadLine.contactSupplierName || '',
      },
      validationSchema: capitalGoodSchema,
      onSubmit: (values) => {
        const submitValues = {
          ...values,
          goodCategoryId: goodCategoryId || values.goodCategoryId,
          category: undefined,
          subCategoryOne: undefined,
          subCategoryTwo: undefined,
        }
        const response = formatTransaction(submitValues, '', true, customFields)
        submitHandler(editUploadLine.id, response)
      }
    })

    //Use Effects
    useEffect(() => {
      goodCategoriesService.getCategoryOptions()
        .then(response => {
          setCategoryOptions(response.options)
        })
        .catch(err => console.log(err))

      measurementUnitService.getAll()
        .then(response => {
          setMeasurementUnits(response)
        })
        .catch(err => console.log(err))

      customFieldService.getAllActive(`&entryType=capitalGood`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))

      ContactSupplierService.getInstance().getActiveSuppliersLite()
        .then(response => setSuppliers(response))
        .catch(err => console.log(err))
    }, [])

    useEffect(() => {
      if (open && editUploadLine.goodCategoryId) {
        goodCategoriesService.getOne(editUploadLine.goodCategoryId)
          .then(response => {
            formik.setFieldValue('category', response.category, false)
            formik.setFieldValue('subCategoryOne', response.subCategoryOne || '', false)
            formik.setFieldValue('subCategoryTwo', response.subCategoryTwo || '', false)
            setGoodCategoryId(response.id)
            formik.setFieldValue('goodCategoryId', response.id, false)
            if (response.category) {
              goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(response.category)}`)
                .then(categoryResponse => {
                  setSubCategorysOneOptions(categoryResponse.options)
                  if (response.subCategoryOne) {
                    goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(response.category)}&subCategoryOne=${encodeURIComponent(response.subCategoryOne)}`)
                      .then(subCategoryResponse => {
                        setSubCategoryTwoOptions(subCategoryResponse.options)
                      })
                      .catch(err => console.log(err))
                  }
                })
                .catch(err => console.log(err))
            }
          })
          .catch(err => console.log(err))
      } else if (open && !editUploadLine.goodCategoryId) {
        formik.setFieldValue('category', '', false)
        formik.setFieldValue('subCategoryOne', '', false)
        formik.setFieldValue('subCategoryTwo', '', false)
        setGoodCategoryId('')
        formik.setFieldValue('goodCategoryId', '', false)
        setSubCategorysOneOptions([])
        setSubCategoryTwoOptions([])
      }
    }, [open, editUploadLine.goodCategoryId])

    useEffect(() => {
      if (customFields && customFields.length && (editUploadLine as any)?.customFieldValues) {
        customFields.forEach(field => {
          const matchingValue = (editUploadLine as any).customFieldValues.find((v: any) => v.customFieldId === field.id)?.value ?? ''
          formik.setFieldValue(field.id, matchingValue, false)
        })
      }
    }, [customFields, editUploadLine])

    useEffect(() => {
      if (formik.values.category) {
        goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(formik.values.category)}`)
          .then(response => {
            setSubCategorysOneOptions(response.options)
          })
          .catch(err => console.log(err))
      }
    }, [formik.values.category])

    useEffect(() => {
      if (formik.values.subCategoryOne) {
        goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(formik.values.category)}&subCategoryOne=${encodeURIComponent(formik.values.subCategoryOne)}`)
          .then(response => {
            setSubCategoryTwoOptions(response.options)
          })
          .catch(err => console.log(err))
      }
    }, [formik.values.subCategoryOne])

    //Form Event Handlers
    const selectCategoryHandler = (e: SelectChangeEvent<string>) => {
      if (e.target.value !== formik.values.category) {
        formik.setFieldValue('subCategoryOne', '')
        setSubCategorysOneOptions([])
        formik.setFieldValue('subCategoryTwo', '')
        setSubCategoryTwoOptions([])
        setGoodCategoryId('')
        formik.setFieldValue('goodCategoryId', '')
        setCategoryOnly(false)
      }
      formik.setFieldValue('category', e.target.value)
      goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(e.target.value)}`)
        .then(response => {
          setSubCategorysOneOptions(response.options)
          if (response.goodCategoryId) {
            setGoodCategoryId(response.goodCategoryId)
            formik.setFieldValue('goodCategoryId', response.goodCategoryId)
            setCategoryOnly(true)
          } else {
            setCategoryOnly(false)
            setGoodCategoryId('')
            formik.setFieldValue('goodCategoryId', '')
          }
        })
        .catch(err => console.log(err))
    }

    const selectSubCategoryOneHandler = (e: SelectChangeEvent<string>) => {
      if (e.target.value !== formik.values.subCategoryOne) {
        formik.setFieldValue('subCategoryTwo', '')
        setSubCategoryTwoOptions([])
        setGoodCategoryId('')
        formik.setFieldValue('goodCategoryId', '')
        setCategoryAndSubCategoryOneOnly(false)
      }
      formik.setFieldValue('subCategoryOne', e.target.value)
      goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(formik.values.category)}&subCategoryOne=${encodeURIComponent(e.target.value)}`)
        .then(response => {
          setSubCategoryTwoOptions(response.options)
          if (response.goodCategoryId) {
            setGoodCategoryId(response.goodCategoryId)
            formik.setFieldValue('goodCategoryId', response.goodCategoryId)
            setCategoryAndSubCategoryOneOnly(true)
          } else {
            setCategoryAndSubCategoryOneOnly(false)
            setGoodCategoryId('')
            formik.setFieldValue('goodCategoryId', '')
          }
        })
        .catch(err => console.log(err))
    }

    const selectSubCategoryTwoHandler = (e: SelectChangeEvent<string>) => {
      if (e.target.value !== formik.values.subCategoryTwo) {
        setGoodCategoryId('')
        formik.setFieldValue('goodCategoryId', '')
      }
      formik.setFieldValue('subCategoryTwo', e.target.value)
      goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(formik.values.category)}&subCategoryOne=${encodeURIComponent(formik.values.subCategoryOne)}&subCategoryTwo=${encodeURIComponent(e.target.value)}`)
        .then(response => {
          if (response.goodCategoryId) {
            setGoodCategoryId(response.goodCategoryId)
            formik.setFieldValue('goodCategoryId', response.goodCategoryId)
          } else {
            setGoodCategoryId('')
            formik.setFieldValue('goodCategoryId', '')
          }
        })
        .catch(err => console.log(err))
    }

    return (
      <>
        <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
          <Box sx={{ p: 4 }} component='form' onSubmit={formik.handleSubmit}>
            <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
              Edit Uploaded Data
            </DialogTitle>
            <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='name'>{screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} Name</InputLabel>
                <OutlinedInput id='name' label={`${screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} Name`} name='name' type='text'
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
                {formik.touched.name && formik.errors.name &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.name as string}</FormHelperText>
                }
              </FormControl>
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='capitalGoodReference'>{screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} Reference</InputLabel>
                <OutlinedInput id='capitalGoodReference' label={`${screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} Reference`} name='capitalGoodReference' type='text'
                  value={formik.values.capitalGoodReference}
                  onChange={formik.handleChange}
                  error={formik.touched.capitalGoodReference && Boolean(formik.errors.capitalGoodReference)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
              </FormControl>
              {formik.touched.capitalGoodReference && formik.errors.capitalGoodReference &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.capitalGoodReference as string}</FormHelperText>
              }
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='description'>{screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} Description</InputLabel>
                <OutlinedInput id='description' label={`${screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} Description`} name='description' type='text'
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
                {formik.touched.description && formik.errors.description &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.description as string}</FormHelperText>
                }
              </FormControl>
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='modelNumber'>Model Number</InputLabel>
                <OutlinedInput id='modelNumber'
                  name='modelNumber'
                  label='Model Number'
                  fullWidth
                  error={formik.touched.modelNumber && Boolean(formik.errors.modelNumber)}
                  value={formik.values.modelNumber}
                  onChange={formik.handleChange}
                  sx={{
                    color: 'text.primary', mr: 2
                  }}></OutlinedInput>
                {formik.touched.modelNumber && formik.errors.modelNumber &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.modelNumber as string}</FormHelperText>
                }
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Type of Good</InputLabel>
                <Select
                  fullWidth
                  label='Type of Good'
                  id='typeOfGood' name='typeOfGood'
                  value={formik.values.typeOfGood}
                  error={formik.touched.typeOfGood && Boolean(formik.errors.typeOfGood)}
                  onChange={formik.handleChange}
                  sx={{ color: 'text.primary', mr: 2 }}>
                  <MenuItem value="Purchased Good or Service">Purchased Good or Service</MenuItem>
                  <MenuItem value="Capital Good">Capital Good</MenuItem>
                  <MenuItem value="Sold Product">Sold Product</MenuItem>
                </Select>
                {formik.touched.typeOfGood && formik.errors.typeOfGood &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.typeOfGood ? <div>{formik.errors.typeOfGood as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
              {categoryOptions &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    fullWidth
                    label='Category'
                    id='category' name='category'
                    value={formik.values.category}
                    onChange={selectCategoryHandler}
                    sx={{ color: 'text.primary', mr: 2 }}>
                    {categoryOptions.sort((a, b) => a.localeCompare(b)).map((category, index) => (
                      <MenuItem key={index} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>}
              {subCategoryOneOptions && subCategoryOneOptions.length > 0 &&
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Subcategory One</InputLabel>
                  <Select
                    fullWidth
                    endAdornment={categoryOnly ? <InputAdornment position="end"><Chip label='Optional' /></InputAdornment> : null}
                    label='Subcategory One'
                    id='subCategoryOne' name='subCategoryOne'
                    value={formik.values.subCategoryOne}
                    onChange={selectSubCategoryOneHandler}
                    sx={{ color: 'text.primary', mr: 2 }}>
                    {subCategoryOneOptions.sort((a, b) => a.localeCompare(b)).map((category, index) => (
                      <MenuItem key={index} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>}
              {subCategoryTwoOptions && subCategoryTwoOptions.length > 0 &&
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Subcategory Two</InputLabel>
                  <Select
                    endAdornment={categoryAndSubCategoryOneOnly ? <InputAdornment position="end"><Chip label='Optional' /></InputAdornment> : null}
                    label='Subcategory Two'
                    fullWidth
                    id='subCategoryTwo' name='subCategoryTwo'
                    value={formik.values.subCategoryTwo}
                    onChange={selectSubCategoryTwoHandler}
                    sx={{ color: 'text.primary', mr: 2 }}>
                    {subCategoryTwoOptions.sort((a, b) => a.localeCompare(b)).map((category, index) => (
                      <MenuItem key={index} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>}
              {formik.touched.goodCategoryId && formik.errors.goodCategoryId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.goodCategoryId as string}</FormHelperText>
              }
              {manufacturers && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <Autocomplete
                  id="manufacturerName"
                  options={manufacturers.sort((a, b) => a.name.localeCompare(b.name))}
                  freeSolo
                  autoHighlight
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                    return (
                      <Box
                        component="li"
                        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                        key={key}
                        {...restProps}
                      >
                        {option.name}
                      </Box>
                    )
                  }}
                  value={manufacturers?.find(manufacturer => manufacturer.name === formik.values.manufacturerName) ? manufacturers?.find(manufacturer => manufacturer.name === formik.values.manufacturerName)?.name : formik.values.manufacturerName}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('manufacturerName', typeof newValue === 'string' ? newValue : newValue?.name || '');
                  }}
                  onInputChange={(event, newInputValue) => {
                    formik.setFieldValue('manufacturerName', newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Manufacturer`}
                      error={formik.touched.manufacturerName && Boolean(formik.errors.manufacturerName)}
                      helperText={formik.touched.manufacturerName && formik.errors.manufacturerName ? (formik.errors.manufacturerName as string) : undefined}
                    />
                  )}
                />
              </FormControl>}
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <SupplierPicker
                  id="contactSupplierName"
                  suppliers={suppliers}
                  freeSolo
                  label={`${screenLabelsContext.getLabel('supplier', { capitalize: 'first' })} (optional)`}
                  value={suppliers.find(supplier => supplier.name === formik.values.contactSupplierName)?.id}
                  inputValue={formik.values.contactSupplierName}
                  onChange={(_id, supplier, freeText) => {
                    formik.setFieldValue('contactSupplierName', freeText ?? (supplier?.name || ''));
                  }}
                  onInputChange={(newInputValue) => {
                    formik.setFieldValue('contactSupplierName', newInputValue);
                  }}
                  error={formik.touched.contactSupplierName && Boolean(formik.errors.contactSupplierName)}
                  helperText={formik.touched.contactSupplierName && formik.errors.contactSupplierName ? (formik.errors.contactSupplierName as string) : undefined}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Unit Type</InputLabel>
                <Select
                  fullWidth
                  label='Unit Type'
                  id='unitName' name='unitName'
                  value={formik.values.unitName}
                  error={formik.touched.unitName && Boolean(formik.errors.unitName)}
                  onChange={formik.handleChange}
                  sx={{ color: 'text.primary', mr: 2 }}>
                  {measurementUnits?.sort((a, b) => a.name.localeCompare(b.name)).map((unit, index) => (
                    <MenuItem key={index} value={unit.name}>{unit.name} </MenuItem>
                  ))}
                </Select>
                {formik.touched.unitName && formik.errors.unitName &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.unitName ? <div>{formik.errors.unitName as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Unit Amount</InputLabel>
                <OutlinedInput id='unitAmount'
                  name='unitAmount'
                  fullWidth
                  type='number'
                  label='Unit Amount'
                  value={formik.values.unitAmount}
                  onChange={formik.handleChange}
                  error={formik.touched.unitAmount && Boolean(formik.errors.unitAmount)}
                  sx={{
                    color: 'text.primary', mr: 2
                  }}></OutlinedInput>
                {formik.touched.unitAmount && formik.errors.unitAmount &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.unitAmount ? <div>{formik.errors.unitAmount as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='kgCarbonEmission'>Emissions per Unit</InputLabel>
                <OutlinedInput inputProps={{ min: 0.01, step: 0.01 }} id='kgCarbonEmission' label='Emissions per Unit' name='kgCarbonEmission' type='number'
                  value={formik.values.kgCarbonEmission}
                  onChange={formik.handleChange}
                  endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>kg CO<sub>2</sub>e</Typography></InputAdornment>}
                  error={formik.touched.kgCarbonEmission && Boolean(formik.errors.kgCarbonEmission)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
                {formik.touched.kgCarbonEmission && formik.errors.kgCarbonEmission &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.kgCarbonEmission as string}</FormHelperText>
                }
              </FormControl>
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='sourceOfEmission'>Source of Emission</InputLabel>
                <OutlinedInput id='sourceOfEmission' label='Source of Emission' name='sourceOfEmission' type='text'
                  value={formik.values.sourceOfEmission}
                  onChange={formik.handleChange}
                  error={formik.touched.sourceOfEmission && Boolean(formik.errors.sourceOfEmission)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
                {formik.touched.sourceOfEmission && formik.errors.sourceOfEmission &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.sourceOfEmission as string}</FormHelperText>
                }
              </FormControl>
              {customFields && customFields.length > 0 && customFields.map((customField, index) => {
                return (
                  <Stack
                    direction='row' spacing={2} key={index} sx={{ mt: 2 }}>
                    <FormControl fullWidth variant='outlined'>
                      <InputLabel htmlFor={customField.id}> {customField.fieldName}</InputLabel>
                      <OutlinedInput id={customField.id} label={customField.fieldName} name={customField.id}
                        type={customField.fieldType === 'number' ? 'number' : 'text'}
                        value={formik.values[customField.id]}
                        onChange={formik.handleChange}
                        error={formik.touched[customField.id] && Boolean(formik.errors[customField.id])}
                        sx={{ color: 'text.primary' }}></OutlinedInput>
                    </FormControl>
                    {formik.touched[customField.id] && formik.errors[customField.id] &&
                      <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                        {formik.errors[customField.id] ? <div>{formik.errors[customField.id] as string}</div> : null}
                      </FormHelperText>
                    }
                  </Stack>
                )
              })}
            </DialogContent >
            <DialogActions sx={{ p: 0 }}>
              <DialogSaveButtons
                disableSaveButton={!goodCategoryId}
                cancelHandler={closeHandler}
                submitHandler={formik.submitForm}
                isSubmitting={isSubmitting}
              />
            </DialogActions>
          </Box >
        </Dialog >
      </>
    )
  }